import {
  Action,
  ActionType,
  Connection,
  FlowOperationType,
  flowStructureUtil,
  FlowVersion,
  LoopOnItemsAction,
  RouterAction,
  Step,
  StepLocationRelativeToParent,
} from '@activepieces/shared';
import { t } from 'i18next';

import {
  BIG_ADD_BUTTON_EDGE_SUFFIX,
  BIG_ADD_BUTTON_NODE_SUFFIX,
  BRANCH_START_EDGE_SUFFIX,
  FLOW_END_EDGE_SUFFIX,
  FLOW_END_NODE_SUFFIX,
  flowUtilConsts,
  LOOP_CLOSE_EDGE_SUFFIX,
  LOOP_RETURN_EDGE_SUFFIX,
  LOOP_RETURN_NODE_SUFFIX,
  LOOP_START_EDGE_SUFFIX,
  NEW_STEP_EDGE_SUFFIX,
  NEW_STEP_NODE_SUFFIX,
  STANDARD_EDGE_SUFFIX,
} from './consts';
import {
  ApBigAddButtonNode,
  ApButtonData,
  ApEdge,
  ApEdgeType,
  ApGraph,
  ApLoopReturnEdge,
  ApLoopStartEdge,
  ApNewStepNode,
  ApNode,
  ApNodeType,
  ApRouterStartEdge,
  ApStraightLineEdge,
} from './types';

import { PieceSelectorOperation } from '@/features/pieces/lib/types';
import { XYPosition } from '@xyflow/react';
import { useBuilderStateContext } from '../../builder-hooks';
import { useCallback } from 'react';

function createFocusStepInGraphParams(stepName: string) {
  return {
    nodes: [{ id: stepName }],
    duration: 1000,
    maxZoom: 1.25,
    minZoom: 1.25,
  };
}

const calculateGraphBoundingBox = (graph: ApGraph) => {
  const minX = Math.min(
    ...graph.nodes
      .filter((node) => flowUtilConsts.doesNodeAffectBoundingBox(node.type))
      .map((node) => node.position.x)
  );
  const minY = Math.min(...graph.nodes.map((node) => node.position.y));
  const maxX = Math.max(
    ...graph.nodes
      .filter((node) => flowUtilConsts.doesNodeAffectBoundingBox(node.type))
      .map((node) => node.position.x + flowUtilConsts.AP_NODE_SIZE.STEP.width)
  );
  const maxY = Math.max(...graph.nodes.map((node) => node.position.y));
  const width = maxX - minX;
  const height = maxY - minY;

  return {
    width,
    height,
    left: -minX + flowUtilConsts.AP_NODE_SIZE.STEP.width / 2,
    right: maxX - flowUtilConsts.AP_NODE_SIZE.STEP.width / 2,
    top: minY,
    bottom: maxY,
  };
};

function distanceToLine(
  candidate: { x: number; y: number },
  p1: { x: number; y: number },
  p2: { x: number; y: number }
): number {
  const num = Math.abs(
    (p2.y - p1.y) * candidate.x -
      (p2.x - p1.x) * candidate.y +
      p2.x * p1.y -
      p2.y * p1.x
  );
  const den = Math.sqrt(Math.pow(p2.y - p1.y, 2) + Math.pow(p2.x - p1.x, 2));
  return num / (den || 1);
}

function getNewStepNodePosition(
  parentStepName: string,
  parentStepPosition: { x: number; y: number },
  flowVersion: FlowVersion
): { x: number; y: number } {
  const targetStepNames = flowStructureUtil.getStepTargets(
    flowVersion,
    parentStepName
  );
  const targetPositions = targetStepNames.map((targetName) => {
    const targetStep = flowStructureUtil.getStep(targetName, flowVersion);
    return {
      x:
        (targetStep?.settings?.position?.x ?? 0) +
        flowUtilConsts.AP_NODE_SIZE.STEP.width / 2,
      y: targetStep?.settings?.position?.y ?? 0,
    };
  });

  const dMin = 100;
  const numCandidates = 1000;
  const radius = flowUtilConsts.AP_NODE_SIZE.STEP.width * 2;
  const candidates: { x: number; y: number }[] = [];
  const { x: posX, y: posY } = parentStepPosition;

  for (let i = 0; i < numCandidates; i++) {
    const angle = Math.PI * (i / numCandidates);
    const candidate = {
      x: posX + radius * Math.cos(angle),
      y: posY + radius * Math.sin(angle),
    };
    candidates.push(candidate);
  }

  let selectedPos = { x: posX, y: posY };
  for (const candidate of candidates) {
    const distances = targetPositions.map((targetPos) =>
      distanceToLine(candidate, { x: posX, y: posY }, targetPos)
    );
    if (distances.every((dist) => dist >= dMin)) {
      selectedPos = candidate;
      break;
    }
  }

  return selectedPos;
}

function connectAllNewStepNodes(
  flowVersion: FlowVersion,
  nodes: ApNode[],
  edges: ApEdge[]
): { nodes: ApNode[]; edges: ApEdge[] } {
  const allSteps = flowStructureUtil.getAllSteps(flowVersion);
  const newNodes = [...nodes];
  const newEdges = [...edges];

  allSteps.forEach((step) => {
    if (step.type === ActionType.ROUTER) return;

    const targets =
      step.type === ActionType.LOOP_ON_ITEMS
        ? flowStructureUtil.getExternalLoopTargets(flowVersion, step.name)
        : flowStructureUtil.getStepTargets(flowVersion, step.name);

    if (targets.length === 0) {
      const positionX = step.settings?.position?.x ?? 0;
      const positionY = step.settings?.position?.y ?? 0;
      const position = {
        x: positionX + flowUtilConsts.AP_NODE_SIZE.STEP.width * 2,
        y: positionY,
      };
      const newStepPosition = {
        x: positionX + flowUtilConsts.AP_NODE_SIZE.STEP.width * 2,
        y: positionY,
      };
      const newStepNode: ApNewStepNode = {
        id: `${step.name}${FLOW_END_NODE_SUFFIX}`,
        type: ApNodeType.NEW_STEP_NODE,
        position,
        data: { parentStepName: step.name, newStepPosition },
        draggable: true,
      };
      const newEdge: ApStraightLineEdge = {
        id: `${step.name}${FLOW_END_EDGE_SUFFIX}`,
        source: step.name,
        target: newStepNode.id,
        type: ApEdgeType.STRAIGHT_LINE,
        data: {
          drawArrowHead: true,
          parentStepName: step.name,
          hideButtons: true,
        },
      };
      newNodes.push(newStepNode);
      newEdges.push(newEdge);
    } else {
      const candidatePos = getNewStepNodePosition(
        step.name,
        step.settings.position,
        flowVersion
      );
      const positionX = step.settings?.position?.x ?? 0;
      const positionY = step.settings?.position?.y ?? 0;
      const newStepPosition = {
        x: positionX + flowUtilConsts.AP_NODE_SIZE.STEP.width * 2,
        y: positionY,
      };
      const newStepNode: ApNewStepNode = {
        id: `${step.name}${NEW_STEP_NODE_SUFFIX}`,
        type: ApNodeType.NEW_STEP_NODE,
        position: candidatePos,
        data: { parentStepName: step.name, newStepPosition: newStepPosition },
        draggable: true,
        hidden: true,
      };
      const newEdge: ApStraightLineEdge = {
        id: `${step.name}${NEW_STEP_EDGE_SUFFIX}`,
        source: step.name,
        target: newStepNode.id,
        type: ApEdgeType.STRAIGHT_LINE,
        data: {
          parentStepName: step.name,
          hideButtons: true,
        },
        hidden: true,
      };
      newNodes.push(newStepNode);
      newEdges.push(newEdge);
    }
  });

  return { nodes: newNodes, edges: newEdges };
}

function getLoopReturnNodePosition(
  loopName: string,
  flowVersion: FlowVersion
): { x: number; y: number } {
  const lastLoopStepName = flowStructureUtil.getLoopLastStepName(
    flowVersion,
    loopName
  );
  const loopStep = flowStructureUtil.getStepOrThrow(loopName, flowVersion);
  const loopStepPosition = loopStep.settings.position;

  if (!lastLoopStepName) {
    return {
      x: loopStepPosition.x + flowUtilConsts.AP_NODE_SIZE.STEP.width * 2.5,
      y: loopStepPosition.y + flowUtilConsts.AP_NODE_SIZE.STEP.height * 4.5,
    };
  }

  const lastLoopStep = flowStructureUtil.getStep(lastLoopStepName, flowVersion);
  const lastLoopStepPosition = lastLoopStep?.settings.position ?? {
    x: 0,
    y: 0,
  };

  return {
    x: lastLoopStepPosition.x + flowUtilConsts.AP_NODE_SIZE.STEP.width * 2.5,
    y: lastLoopStepPosition.y + flowUtilConsts.AP_NODE_SIZE.STEP.height * 4.5,
  };
}

function buildNodes(flowVersion: FlowVersion): ApNode[] {
  const allSteps = flowStructureUtil.getAllSteps(flowVersion);
  const nodes: ApNode[] = [];

  function buildStepNode(step: Step): ApNode {
    return {
      id: step.name,
      type: ApNodeType.STEP,
      position: {
        x: step.settings?.position?.x ?? 0,
        y: step.settings?.position?.y ?? 0,
      },
      data: { step },
      selectable: true,
      draggable: true,
      style: { pointerEvents: 'all' },
    };
  }

  function buildLoopNodes(step: LoopOnItemsAction) {
    const baseX = step.settings?.position?.x ?? 0;
    const baseY = step.settings?.position?.y ?? 0;
    if (!step.subflow || step.subflow.actions.length === 0) {
      const bigAddButtonNode: ApBigAddButtonNode = {
        id: `${step.name}${BIG_ADD_BUTTON_NODE_SUFFIX}`,
        type: ApNodeType.BIG_ADD_BUTTON,
        position: {
          x: baseX + flowUtilConsts.AP_NODE_SIZE.STEP.width * 2,
          y: baseY + flowUtilConsts.AP_NODE_SIZE.STEP.width * 2,
        },
        data: {
          edgeId: `${step.name}${BIG_ADD_BUTTON_NODE_SUFFIX}`,
          parentStepName: step.name,
          stepLocationRelativeToParent:
            StepLocationRelativeToParent.INSIDE_LOOP,
          newStepPosition: {
            x: baseX + flowUtilConsts.AP_NODE_SIZE.STEP.width * 2,
            y: baseY + flowUtilConsts.AP_NODE_SIZE.STEP.width * 2,
          },
        },
        selectable: false,
        style: { pointerEvents: 'all' },
      };
      nodes.push(bigAddButtonNode);
      nodes.push({
        id: `${step.name}${LOOP_RETURN_NODE_SUFFIX}`,
        type: ApNodeType.LOOP_RETURN_NODE,
        position: getLoopReturnNodePosition(step.name, flowVersion),
        data: {},
        selectable: false,
      });
    } else {
      createNodesFromSteps(step.subflow.actions);
      nodes.push({
        id: `${flowStructureUtil.getLoopLastStepName(
          flowVersion,
          step.name
        )}${LOOP_RETURN_NODE_SUFFIX}`,
        type: ApNodeType.LOOP_RETURN_NODE,
        position: getLoopReturnNodePosition(step.name, flowVersion),
        data: {},
        selectable: false,
      });
    }
  }

  function buildRouterNodes(step: RouterAction) {
    const baseX = step.settings?.position?.x ?? 0;
    const baseY = step.settings?.position?.y ?? 0;
    const branches = step.children;
    branches.forEach((branch, index) => {
      if (!branch || branch.actions.length === 0) {
        const bigAddButtonNode: ApBigAddButtonNode = {
          id: `${step.name}-${index}${BIG_ADD_BUTTON_NODE_SUFFIX}`,
          type: ApNodeType.BIG_ADD_BUTTON,
          position: {
            x: baseX + flowUtilConsts.AP_NODE_SIZE.STEP.width * 4,
            y: baseY + flowUtilConsts.AP_NODE_SIZE.STEP.width * 1.5 * index,
          },
          data: {
            edgeId: `${step.name}-${index}${BIG_ADD_BUTTON_EDGE_SUFFIX}`,
            parentStepName: step.name,
            stepLocationRelativeToParent:
              StepLocationRelativeToParent.INSIDE_BRANCH,
            branchIndex: index,
            newStepPosition: {
              x: baseX + flowUtilConsts.AP_NODE_SIZE.STEP.width * 4,
              y:
                baseY +
                flowUtilConsts.AP_NODE_SIZE.STEP.width * 1.5 * index -
                flowUtilConsts.LABEL_HEIGHT,
            },
          },
          selectable: false,
          style: { pointerEvents: 'all' },
        };
        nodes.push(bigAddButtonNode);
      } else {
        createNodesFromSteps(branch.actions);
      }
    });
  }

  function createNodesFromSteps(steps: Step[]) {
    steps.forEach((step) => {
      nodes.push(buildStepNode(step));

      if (step.type === ActionType.LOOP_ON_ITEMS) {
        buildLoopNodes(step);
      }

      if (step.type === ActionType.ROUTER) {
        buildRouterNodes(step);
      }
    });
  }

  createNodesFromSteps(allSteps);

  return nodes;
}

function buildEdges(flowVersion: FlowVersion): ApEdge[] {
  const edges: ApEdge[] = [];
  const steps = flowStructureUtil.getAllSteps(flowVersion);
  const { loopFirstElements, loopLastElements } =
    preprocessLoopConnections(flowVersion);
  const { routerBranchFirstElements } =
    preprocessRouterConnections(flowVersion);

  function buildLoopEdges(step: LoopOnItemsAction) {
    const isEmptyLoop = !step.subflow || step.subflow.actions.length === 0;
    const firstLoopActionName = loopFirstElements.get(step.name);
    const lastLoopActionName = loopLastElements.get(step.name);

    const loopStartEdge: ApLoopStartEdge = {
      id: `${step.name}${LOOP_START_EDGE_SUFFIX}`,
      source: step.name,
      target: isEmptyLoop
        ? `${step.name}${BIG_ADD_BUTTON_NODE_SUFFIX}`
        : firstLoopActionName!,
      type: ApEdgeType.LOOP_START_EDGE,
      data: {
        isLoopEmpty: isEmptyLoop,
        parentStepName: step.name,
      },
    };
    edges.push(loopStartEdge);

    const loopReturnEdge: ApLoopReturnEdge = {
      id: `${step.name}${LOOP_RETURN_EDGE_SUFFIX}`,
      source: isEmptyLoop
        ? `${step.name}${BIG_ADD_BUTTON_NODE_SUFFIX}`
        : lastLoopActionName!,
      target: isEmptyLoop
        ? `${step.name}${LOOP_RETURN_NODE_SUFFIX}`
        : `${flowStructureUtil.getLoopLastStepName(
            flowVersion,
            step.name
          )}${LOOP_RETURN_NODE_SUFFIX}`,
      type: ApEdgeType.LOOP_RETURN_EDGE,
      data: {
        parentStepName: lastLoopActionName ?? step.name,
        isLoopEmpty: isEmptyLoop,
        drawArrowHeadAfterEnd: !isEmptyLoop,
        verticalSpaceBetweenReturnNodeStartAndEnd: 50,
      },
    };
    edges.push(loopReturnEdge);

    const loopCloseEdge: ApEdge = {
      id: `${step.name}${LOOP_CLOSE_EDGE_SUFFIX}`,
      source: isEmptyLoop
        ? `${step.name}${LOOP_RETURN_NODE_SUFFIX}`
        : `${flowStructureUtil.getLoopLastStepName(
            flowVersion,
            step.name
          )}${LOOP_RETURN_NODE_SUFFIX}`,
      target: step.name,
      type: ApEdgeType.LOOP_CLOSE_EDGE,
      data: {
        parentStepName: step.name,
        isLoopEmpty: isEmptyLoop,
        drawArrowHeadAfterEnd: false,
        verticalSpaceBetweenReturnNodeStartAndEnd: 50,
      },
    };
    edges.push(loopCloseEdge);
  }

  function buildRouterEdges(step: RouterAction) {
    const branches = step.children;
    branches.forEach((branch, index) => {
      const isEmptyBranch = !branch || branch.actions.length === 0;
      const firstBranchName = routerBranchFirstElements
        .get(step.name)
        ?.get(index);
      const branchStartEdge: ApRouterStartEdge = {
        id: `${step.name}-${index}${BRANCH_START_EDGE_SUFFIX}`,
        source: step.name,
        target: isEmptyBranch
          ? `${step.name}-${index}${BIG_ADD_BUTTON_NODE_SUFFIX}`
          : firstBranchName!,
        type: ApEdgeType.ROUTER_START_EDGE,
        data: {
          isBranchEmpty: isEmptyBranch,
          label:
            step.settings.branches[index]?.branchName ??
            `${t('Branch')} ${index + 1} (missing branch)`,
          branchIndex: index,
          stepLocationRelativeToParent:
            StepLocationRelativeToParent.INSIDE_BRANCH,
          drawHorizontalLine: index === 0 || index === branches.length - 1,
          drawStartingVerticalLine: index === 0,
        },
      };
      edges.push(branchStartEdge);
    });
  }

  function buildStandardEdges(step: Step) {
    const stepTargets = flowStructureUtil.getStepTargets(
      flowVersion,
      step.name
    );
    stepTargets.forEach((targetName) => {
      if (
        Array.from(loopFirstElements.values()).includes(targetName) ||
        Array.from(loopLastElements.values()).includes(step.name)
      ) {
        return;
      }

      if (step.type === ActionType.ROUTER) {
        const branchFirstMap = routerBranchFirstElements.get(step.name);
        if (
          branchFirstMap &&
          Array.from(branchFirstMap.values()).includes(targetName)
        ) {
          return;
        }
      }

      edges.push({
        id: `${step.name}-${targetName}${STANDARD_EDGE_SUFFIX}`,
        source: step.name,
        target: targetName,
        type: ApEdgeType.STRAIGHT_LINE,
        data: {
          drawArrowHead: true,
          parentStepName: step.name,
        },
        selectable: true,
        focusable: true,
      });
    });
  }

  steps.forEach((step) => {
    if (step.type === ActionType.LOOP_ON_ITEMS) {
      buildLoopEdges(step);
    }
    if (step.type === ActionType.ROUTER) {
      buildRouterEdges(step);
    }
    buildStandardEdges(step);
  });

  return edges;
}

function preprocessLoopConnections(flowVersion: FlowVersion) {
  const loopFirstElements = new Map<string, string>();
  const loopLastElements = new Map<string, string>();

  flowStructureUtil.getAllSteps(flowVersion).forEach((action) => {
    if (action.type === ActionType.LOOP_ON_ITEMS && action.subflow) {
      const firstLoopElement = flowStructureUtil.getLoopFirstStepName(
        flowVersion,
        action.name
      );
      const lastLoopElement = flowStructureUtil.getLoopLastStepName(
        flowVersion,
        action.name
      );
      if (firstLoopElement)
        loopFirstElements.set(action.name, firstLoopElement);
      if (lastLoopElement) loopLastElements.set(action.name, lastLoopElement);
    }
  });

  return { loopFirstElements, loopLastElements };
}

function preprocessRouterConnections(flowVersion: FlowVersion) {
  const routerBranchFirstElements = new Map<string, Map<number, string>>();
  const routerBranchLastElements = new Map<string, Map<number, string>>();

  flowStructureUtil.getAllSteps(flowVersion).forEach((action) => {
    if (action.type === ActionType.ROUTER && Array.isArray(action.children)) {
      const firstMap = new Map<number, string>();
      const lastMap = new Map<number, string>();
      action.children.forEach((child, branchIndex) => {
        if (child && Array.isArray(child.actions) && child.actions.length > 0) {
          const firstElement = flowStructureUtil.getBranchFirstStepName(
            flowVersion,
            action.name,
            branchIndex
          );
          if (firstElement) {
            firstMap.set(branchIndex, firstElement);
          }
          lastMap.set(
            branchIndex,
            child.actions[child.actions.length - 1].name
          );
        }
      });
      if (firstMap.size > 0) {
        routerBranchFirstElements.set(action.name, firstMap);
      }
      if (lastMap.size > 0) {
        routerBranchLastElements.set(action.name, lastMap);
      }
    }
  });

  return { routerBranchFirstElements, routerBranchLastElements };
}

function getOperationForAddButton(data: ApButtonData): PieceSelectorOperation {
  if (
    data.stepLocationRelativeToParent ===
    StepLocationRelativeToParent.INSIDE_BRANCH
  ) {
    return {
      type: FlowOperationType.ADD_ACTION,
      actionLocation: {
        parentStep: data.parentStepName,
        stepLocationRelativeToParent: data.stepLocationRelativeToParent,
        branchIndex: data.branchIndex,
      },
      newStepPosition: data.newStepPosition,
    };
  }

  const baseOperation: PieceSelectorOperation = {
    type: FlowOperationType.ADD_ACTION,
    actionLocation: {
      parentStep: data.parentStepName,
      stepLocationRelativeToParent: data.stepLocationRelativeToParent,
    },
    newStepPosition: data.newStepPosition,
  };

  if (
    data.stepLocationRelativeToParent !==
    StepLocationRelativeToParent.INSIDE_LOOP
  ) {
    baseOperation.connection = data.connection;
  }

  return baseOperation;
}

export const getOrderedActionsIds = (
  connections: Connection[] = [],
  parent: string
): string[] => {
  const connectionMap = new Map(
    connections.map((connection) => [connection.sourceId, connection.targetId])
  );
  const orderedActionsIds: string[] = [];
  let currentAction = connectionMap.get(parent);

  while (currentAction && !orderedActionsIds.includes(currentAction)) {
    orderedActionsIds.push(currentAction);
    currentAction = connectionMap.get(currentAction);
  }

  if (currentAction) {
    console.warn(
      `Warning: Infinite loop detected at step "${currentAction}". Halting traversal.`
    );
  }

  return orderedActionsIds;
};

/**
 * Computes the auto-layout position of every action, without any side effect.
 * Callers decide whether to persist them (DRAG_STEP) or apply them locally.
 */
const computeLayoutPositions = (
  flowVersion: FlowVersion,
): Map<string, XYPosition> => {
  const LAYOUT_STEP_SPACING = flowUtilConsts.AP_NODE_SIZE.STEP.width * 2;
  const LAYOUT_VERTICAL_SPACING_ROUTER =
    flowUtilConsts.AP_NODE_SIZE.STEP.width * 1.5;
  const positions = new Map<string, XYPosition>();

  const walk = (action: Action, position: XYPosition) => {
    positions.set(action.name, position);

    switch (action.type) {
      case ActionType.ROUTER:
        action.children?.forEach((branch, branchIndex) => {
          if (!branch) return;
          const actionsMap = new Map(
            branch.actions.map((child) => [child.name, child]),
          );
          getOrderedActionsIds(branch.connections, action.name).forEach(
            (actionId, childIndex) => {
              const childAction = actionsMap.get(actionId);
              if (!childAction) return;
              walk(childAction, {
                x: position.x + LAYOUT_STEP_SPACING * (2 + childIndex),
                y:
                  position.y +
                  LAYOUT_VERTICAL_SPACING_ROUTER * branchIndex -
                  flowUtilConsts.LABEL_HEIGHT,
              });
            },
          );
        });
        break;

      case ActionType.LOOP_ON_ITEMS: {
        const actionsMap = new Map(
          action.subflow?.actions.map((child) => [child.name, child]),
        );
        getOrderedActionsIds(action.subflow?.connections, action.name).forEach(
          (actionId, subflowIndex) => {
            const subflowAction = actionsMap.get(actionId);
            if (!subflowAction) return;
            walk(subflowAction, {
              x: position.x + LAYOUT_STEP_SPACING * (subflowIndex + 1),
              y: position.y + LAYOUT_STEP_SPACING,
            });
          },
        );
        break;
      }

      default:
        break;
    }
  };

  const trigger = flowVersion?.triggers?.[0];
  if (!trigger) return positions;

  const triggerPosition = trigger.settings.position;
  const actionsMap = new Map(
    flowVersion.actions.map((action) => [action.name, action]),
  );
  getOrderedActionsIds(flowVersion.connections, trigger.name).forEach(
    (actionId, index) => {
      const action = actionsMap.get(actionId);
      if (!action) return;
      walk(action, {
        x: triggerPosition.x + LAYOUT_STEP_SPACING * (index + 1),
        y: triggerPosition.y,
      });
    },
  );

  return positions;
};

const useResetNodesPosition = () => {
  const [flowVersion, applyOperation] = useBuilderStateContext((state) => [
    state.flowVersion,
    state.applyOperation,
  ]);

  const STEP_SPACING = flowUtilConsts.AP_NODE_SIZE.STEP.width * 2;
  const VERTICAL_SPACING_ROUTER = flowUtilConsts.AP_NODE_SIZE.STEP.width * 1.5;

  const processNodePosition = useCallback(
    (action: Action, position: XYPosition) => {
      applyOperation(
        {
          type: FlowOperationType.DRAG_STEP,
          request: {
            stepName: action.name,
            newPosition: position,
          },
        },
        () => console.error('Drag step operation failed')
      );

      switch (action.type) {
        case ActionType.ROUTER:
          action.children?.forEach((branch, branchIndex) => {
            if (!branch) return;
            const actionsMap = new Map(
              branch.actions.map((action) => [action.name, action])
            );

            const orderedChildrenActionsIds = getOrderedActionsIds(
              branch.connections,
              action.name
            );
            orderedChildrenActionsIds.forEach((actionId, childIndex) => {
              const childAction = actionsMap.get(actionId);
              if (!childAction) return;

              const childPosition: XYPosition = {
                x: position.x + STEP_SPACING * (2 + childIndex),
                y:
                  position.y +
                  VERTICAL_SPACING_ROUTER * branchIndex -
                  flowUtilConsts.LABEL_HEIGHT,
              };
              processNodePosition(childAction, childPosition);
            });
          });
          break;

        case ActionType.LOOP_ON_ITEMS:
          const actionsMap = new Map(
            action.subflow?.actions.map((action) => [action.name, action])
          );

          const orderedSubflowActionsIds = getOrderedActionsIds(
            action.subflow?.connections,
            action.name
          );
          orderedSubflowActionsIds.forEach((actionId, subflowIndex) => {
            const subflowAction = actionsMap.get(actionId);
            if (!subflowAction) return;

            const subflowPosition: XYPosition = {
              x: position.x + STEP_SPACING * (subflowIndex + 1),
              y: position.y + STEP_SPACING,
            };
            processNodePosition(subflowAction, subflowPosition);
          });
          break;

        default:
          break;
      }
    },
    [applyOperation]
  );

  return useCallback(() => {
    const trigger = flowVersion?.triggers?.[0];
    if (!trigger) return;

    const triggerPosition = trigger.settings.position;
    const { connections, actions } = flowVersion;
    const actionsMap = new Map(actions.map((action) => [action.name, action]));
    const orderedActionsIds = getOrderedActionsIds(connections, trigger.name);

    orderedActionsIds.forEach((actionId, index) => {
      const action = actionsMap.get(actionId);
      if (!action) return;

      const newPosition: XYPosition = {
        x: triggerPosition.x + STEP_SPACING * (index + 1),
        y: triggerPosition.y,
      };
      processNodePosition(action, newPosition);
    });
  }, [flowVersion, processNodePosition]);
};

export const flowCanvasUtils = {
  convertFlowVersionToGraph(flowVersion: FlowVersion): ApGraph {
    const nodes = buildNodes(flowVersion);
    const edges = buildEdges(flowVersion);

    const { nodes: finalNodes, edges: finalEdges } = connectAllNewStepNodes(
      flowVersion,
      nodes,
      edges
    );
    return {
      nodes: finalNodes,
      edges: finalEdges,
    };
  },
  createFocusStepInGraphParams,
  calculateGraphBoundingBox,
  getNewStepNodePosition,
  getLoopReturnNodePosition,
  getOperationForAddButton,
  getOrderedActionsIds,
  computeLayoutPositions,
  useResetNodesPosition,
};
